import logger from "@utils/logger";
import type { AnalyticsProvider } from "./types";

export type ProviderName = "statsig" | "posthog";

const raw = (import.meta.env.VITE_ANALYTICS_TOOL ?? "").trim();
let activeProvider: ProviderName | null = raw === "statsig" || raw === "posthog" ? raw : null;

// Loud-but-non-fatal: if the env is unset or typo'd, every event silently
// no-ops. Surface that at startup so a misconfigured deploy is obvious in
// the console / Sentry breadcrumbs instead of a "no analytics" mystery.
if (activeProvider === null) {
  logger._console.warn(
    `[analytics] VITE_ANALYTICS_TOOL is ${raw ? `"${raw}" (invalid)` : "unset"}; ` +
      `expected "statsig" or "posthog". All analytics events will be no-ops.`,
  );
}

const registered = new Map<ProviderName, AnalyticsProvider>();

/**
 * Providers self-register here at module load (see `providers/index.ts`).
 * Keeps the registry agnostic of any concrete provider implementation —
 * no `import { posthogProvider }` needed in this file.
 */
export function registerProvider(name: ProviderName, provider: AnalyticsProvider): void {
  registered.set(name, provider);
}

export function getActiveProviderName(): ProviderName | null {
  return activeProvider;
}

export function isProviderEnabled(name: ProviderName): boolean {
  return activeProvider === name;
}

/**
 * Returns the active provider's instance, or null if no provider is enabled
 * or the active one has not been registered yet. Callers MUST ensure
 * `providers/index.ts` (or an equivalent side-effect import) has run before
 * relying on a non-null return.
 */
export function getActiveProvider(): AnalyticsProvider | null {
  return activeProvider ? registered.get(activeProvider) ?? null : null;
}

/**
 * Test-only: override the active provider name. Throws in non-test builds
 * to prevent accidental runtime use. The env-derived value is read once at
 * module load, so this is the only sanctioned way to swap providers in tests.
 */
export function __setActiveProviderForTests(name: ProviderName | null): void {
  if (import.meta.env.MODE !== "test") {
    throw new Error("__setActiveProviderForTests is only available in test builds");
  }
  activeProvider = name;
}
